import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Checkbox, Chip, CircularProgress,
  Divider, FormControl, FormControlLabel, FormHelperText, IconButton,
  InputLabel, LinearProgress, MenuItem, Select, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle, CloudUpload, Delete, Edit, ErrorOutline, Movie, PlayArrow,
  Refresh, Schedule, SkipNext, Upload,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Panel from '../crt/Panel';
import ShortsClipEditor from './ShortsClipEditor';
import ShortsFramingEditor from './ShortsFramingEditor';
import ShortsPreview from './ShortsPreview';
import RenderJobBadge from './RenderJobBadge';
import ShortsCalendar from './ShortsCalendar';
import ShortsRotationPanel, { RotationStrip } from './ShortsRotationPanel';
import RotationCalendar from './RotationCalendar';
import ThumbnailFacePicker from './ThumbnailFacePicker';
import { isOverdue, troubles } from './shortsTrouble';
import {
  uploadsAPI, shortsAPI, ShortsBatch, ShortsClip, ShortsClipIn,
  ShortsPlatformCap, ShortsTarget,
} from '../../services/api';

type Site = 'spoutlore' | 'mallbrats';

/** Per-file state while a batch is being staged to S3. */
interface Staging {
  file: File;
  pct: number;
  status: 'queued' | 'uploading' | 'staged' | 'failed';
  error?: string;
  staged?: { bucket: string; object_key: string };
}

const STATUS_COLOR: Record<ShortsTarget['status'],
  'default' | 'info' | 'warning' | 'success' | 'error'> = {
  pending: 'default',
  uploading: 'info',
  scheduled: 'warning',
  published: 'success',
  error: 'error',
  skipped: 'default',
};

const fmtDur = (s: number | null | undefined) =>
  s == null ? '—' : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;

const fmtWhen = (iso: string | null | undefined) =>
  !iso ? '—' : new Date(iso).toLocaleString(undefined,
    { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const todayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);          // default: start tomorrow
  return d.toISOString().slice(0, 10);
};

const ShortsBatchTab: React.FC = () => {
  const qc = useQueryClient();

  const [site, setSite] = useState<Site>('spoutlore');
  const [label, setLabel] = useState('');
  const [episodeKey, setEpisodeKey] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<ShortsClip | null>(null);
  const [framing, setFraming] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<ShortsClip | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const [picking, setPicking] = useState<{ key: string; title: string } | null>(null);
  const [platforms, setPlatforms] = useState<string[]>(['youtube']);
  const [staging, setStaging] = useState<Staging[]>([]);
  const [busy, setBusy] = useState(false);
  const [ingestReport, setIngestReport] = useState<
    { accepted: number; rejected: Array<{ filename: string; reasons: string[] }>;
      skipped: Array<{ filename: string; platform: string; reason: string }> } | null>(null);
  const [startDate, setStartDate] = useState(todayISO());
  const [timeOfDay, setTimeOfDay] = useState('09:00');
  const [intervalDays, setIntervalDays] = useState(1);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const caps = useQuery({
    queryKey: ['shorts', 'platforms'],
    queryFn: shortsAPI.getPlatforms,
    staleTime: 5 * 60_000,
  });

  const batches = useQuery({
    queryKey: ['shorts', 'batches'],
    queryFn: () => shortsAPI.listBatches(50),
    refetchInterval: 30_000,
  });

  const detail = useQuery({
    queryKey: ['shorts', 'batch', selected],
    queryFn: () => shortsAPI.getBatch(selected as string),
    enabled: !!selected,
    // Targets move on the 5-minute cron; poll often enough to feel live.
    // While a re-render is queued or rendering, poll faster so the badge
    // actually tracks it — a stage indicator that updates every 20s reads as
    // stuck during a render that takes about two minutes.
    refetchInterval: (query) => {
      const clips = (query.state.data as any)?.clips ?? [];
      const busy = clips.some((c: any) =>
        c.render_job && ['queued', 'rendering'].includes(c.render_job.status));
      return busy ? 5_000 : 20_000;
    },
  });

  // Thumbnail/title rotation: what's live, what's next, did each swap land.
  // The swap worker runs every 20 minutes, so a minute is plenty.
  const rotation = useQuery({
    queryKey: ['shorts', 'rotation', selected],
    queryFn: () => shortsAPI.getBatchRotation(selected as string),
    enabled: !!selected,
    refetchInterval: 60_000,
  });
  const rotationByClip = useMemo(() => Object.fromEntries(
    (rotation.data?.clips ?? []).map((c) => [c.clip_id, c])), [rotation.data]);
  const clipLabels = useMemo(() => Object.fromEntries(
    (detail.data?.clips ?? []).map((c) => [c.clip_id, `${c.ordinal + 1}`])), [detail.data]);

  const status = useQuery({
    queryKey: ['shorts', 'status'],
    queryFn: shortsAPI.getStatus,
    refetchInterval: 60_000,
  });

  const capList: ShortsPlatformCap[] = useMemo(
    () => caps.data?.platforms ?? [], [caps.data]);

  const retry = useMutation({
    mutationFn: (targetId: string) => shortsAPI.retryTarget(targetId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shorts', 'batch', selected] });
      qc.invalidateQueries({ queryKey: ['shorts', 'status'] });
    },
  });

  const removeClip = useMutation({
    mutationFn: (clipId: string) => shortsAPI.deleteClip(clipId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shorts', 'batch', selected] });
      qc.invalidateQueries({ queryKey: ['shorts', 'batches'] });
    },
  });

  const togglePlatform = (p: string) =>
    setPlatforms((cur) =>
      cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]);

  const onPick = (files: FileList | null) => {
    if (!files?.length) return;
    // Sort by filename so an export's `01-`, `02-` prefixes become the
    // release order without the editor having to drag anything around.
    const picked = Array.from(files)
      .filter((f) => f.type.startsWith('video/') || f.name.toLowerCase().endsWith('.mp4'))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    setStaging(picked.map((file) => ({ file, pct: 0, status: 'queued' })));
    setIngestReport(null);
  };

  /**
   * Stage every picked clip in S3, then register the batch.
   *
   * Registration is where ffprobe validation happens server-side: a clip that
   * can't be a Short (landscape, >3min, silent, wrong codec) is REJECTED and
   * reported here rather than failing days later at release time.
   */
  const uploadAndRegister = useCallback(async () => {
    if (!staging.length || !platforms.length) return;
    setBusy(true);
    setIngestReport(null);
    try {
      const batch = await shortsAPI.createBatch({
        site,
        label: label || `${site} shorts ${new Date().toLocaleDateString()}`,
        episode_key: episodeKey || null,
      });

      const stagedClips: ShortsClipIn[] = [];
      for (let i = 0; i < staging.length; i++) {
        const entry = staging[i];
        setStaging((cur) => cur.map((s, idx) =>
          idx === i ? { ...s, status: 'uploading' } : s));
        try {
          const res = await uploadsAPI.uploadToS3Multipart(
            site, 'short', entry.file,
            (pct: number) => setStaging((cur) => cur.map((s, idx) =>
              idx === i ? { ...s, pct } : s)),
          );
          stagedClips.push({
            bucket: res.bucket,
            object_key: res.object_key,
            filename: entry.file.name,
          });
          setStaging((cur) => cur.map((s, idx) => idx === i
            ? { ...s, status: 'staged', pct: 100, staged: { bucket: res.bucket, object_key: res.object_key } }
            : s));
        } catch (e: any) {
          // One bad upload must not lose the rest of the batch.
          setStaging((cur) => cur.map((s, idx) => idx === i
            ? { ...s, status: 'failed', error: e?.message || String(e) } : s));
        }
      }

      if (!stagedClips.length) {
        setIngestReport({ accepted: 0, rejected: [], skipped: [] });
        return;
      }

      const reg = await shortsAPI.registerClips(batch.batch_id, stagedClips, platforms);
      const skipped: Array<{ filename: string; platform: string; reason: string }> = [];
      reg.accepted.forEach((a) => {
        Object.entries(a.skipped_platforms || {}).forEach(([platform, reason]) =>
          skipped.push({ filename: a.filename, platform, reason: String(reason) }));
      });
      setIngestReport({ accepted: reg.accepted_count, rejected: reg.rejected, skipped });

      if (reg.accepted_count) {
        await shortsAPI.scheduleBatch(batch.batch_id, {
          start_date: new Date(`${startDate}T00:00:00`).toISOString(),
          time_of_day: timeOfDay,
          interval_days: intervalDays,
          timezone: caps.data?.defaults.timezone || 'America/Los_Angeles',
          platforms,
        });
      }

      setSelected(batch.batch_id);
      setStaging([]);
      qc.invalidateQueries({ queryKey: ['shorts', 'batches'] });
    } finally {
      setBusy(false);
    }
  }, [staging, platforms, site, label, episodeKey, startDate, timeOfDay,
      intervalDays, caps.data, qc]);

  const stuck = status.data?.stuck_count ?? 0;

  return (
    <Stack spacing={3}>
      {/* ── Anything overdue is the one thing worth interrupting for ── */}
      {stuck > 0 && (
        <Alert severity="error" icon={<ErrorOutline />}>
          <AlertTitle>{stuck} short{stuck === 1 ? '' : 's'} overdue</AlertTitle>
          These passed their release slot more than 24h ago and still haven't
          published — the shorts_publish cron isn't keeping up. Check the job in
          Ops, then retry the affected targets below.
        </Alert>
      )}

      {/* Release calendar — collapsible, above the staging panel. Uses the
          shared ReleaseCalendar so this page matches the other calendar views. */}
      <ShortsCalendar />

      {/* ── STEP 1: stage a batch ─────────────────────────────────── */}
      <Panel label="STEP 1 :: STAGE A BATCH">
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Site</InputLabel>
              <Select
                label="Site" value={site}
                disabled={busy || staging.length > 0}
                onChange={(e) => setSite(e.target.value as Site)}
              >
                <MenuItem value="spoutlore">Spout Lore</MenuItem>
                <MenuItem value="mallbrats">Mall Brats</MenuItem>
              </Select>
              {staging.length > 0 && (
                <FormHelperText>Locked — clips are staged for this site</FormHelperText>
              )}
            </FormControl>
            <TextField
              size="small" label="Batch label" value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="S14E07 shorts" sx={{ minWidth: 220 }}
            />
            <TextField
              size="small" label="Episode (SxxEyy)" value={episodeKey}
              onChange={(e) => setEpisodeKey(e.target.value)}
              placeholder="S14E07" sx={{ minWidth: 150 }}
            />
          </Stack>

          <Box>
            <Typography variant="caption" color="text.secondary">
              PLATFORMS
            </Typography>
            <Stack direction="row" flexWrap="wrap" sx={{ mt: 0.5 }}>
              {capList.map((c) => (
                <Tooltip
                  key={c.platform}
                  title={
                    !c.implemented
                      ? 'Adapter not shipped yet — targets will be recorded as skipped'
                      : c.native_schedule
                        ? 'This platform holds the release timer itself, so the clip goes live on schedule even if AutoPod is down'
                        : 'AutoPod holds the release timer for this platform'
                  }
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={platforms.includes(c.platform)}
                        disabled={busy || !c.implemented}
                        onChange={() => togglePlatform(c.platform)}
                      />
                    }
                    label={
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <span>{c.platform}</span>
                        {c.native_schedule && (
                          <Chip size="small" label="native schedule" variant="outlined"
                                sx={{ height: 16, fontSize: 9 }} />
                        )}
                        {!c.implemented && (
                          <Chip size="small" label="soon" sx={{ height: 16, fontSize: 9 }} />
                        )}
                        {c.max_seconds != null && (
                          <Chip size="small" variant="outlined"
                                label={`≤${c.max_seconds}s`}
                                sx={{ height: 16, fontSize: 9 }} />
                        )}
                      </Stack>
                    }
                  />
                </Tooltip>
              ))}
            </Stack>
          </Box>

          <Divider />

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
            <TextField
              size="small" type="date" label="First release"
              value={startDate} onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small" type="time" label="Time of day"
              value={timeOfDay} onChange={(e) => setTimeOfDay(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={caps.data?.defaults.timezone || 'America/Los_Angeles'}
            />
            <TextField
              size="small" type="number" label="Days apart"
              value={intervalDays}
              onChange={(e) => setIntervalDays(Math.max(0, Number(e.target.value)))}
              inputProps={{ min: 0, max: 90 }}
              sx={{ width: 120 }}
              helperText="1 = one per day"
            />
          </Stack>

          <input
            ref={fileInput} type="file" accept="video/mp4,video/*" multiple
            hidden onChange={(e) => onPick(e.target.files)}
          />
          <Box
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onPick(e.dataTransfer.files); }}
            onClick={() => fileInput.current?.click()}
            sx={{
              border: '1px dashed', borderColor: 'divider', borderRadius: 1,
              p: 4, textAlign: 'center', cursor: busy ? 'default' : 'pointer',
              opacity: busy ? 0.5 : 1,
            }}
          >
            <CloudUpload sx={{ fontSize: 32, opacity: 0.6 }} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Drop the episode's shorts here, or click to pick them
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Vertical MP4 (h264/aac), 3 min max. Files are ordered by name, so
              <code> 01-…, 02-…</code> becomes the release order.
            </Typography>
          </Box>

          {staging.length > 0 && (
            <Stack spacing={1}>
              {staging.map((s, i) => (
                <Box key={`${s.file.name}-${i}`}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {i + 1}. {s.file.name} ({(s.file.size / 1024 / 1024).toFixed(1)} MB)
                    </Typography>
                    <Typography variant="caption" color={
                      s.status === 'failed' ? 'error.main'
                        : s.status === 'staged' ? 'success.main' : 'text.secondary'}>
                      {s.status === 'failed' ? `failed: ${s.error}` : s.status}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate" value={s.pct}
                    color={s.status === 'failed' ? 'error' : 'primary'}
                    sx={{ height: 3 }}
                  />
                </Box>
              ))}
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained" startIcon={busy ? <CircularProgress size={14} /> : <Upload />}
                  disabled={busy || !platforms.length}
                  onClick={uploadAndRegister}
                >
                  {busy ? 'Staging…' : `Upload + schedule ${staging.length} clip(s)`}
                </Button>
                <Button disabled={busy} onClick={() => { setStaging([]); setIngestReport(null); }}>
                  Clear
                </Button>
              </Stack>
              {!platforms.length && (
                <Typography variant="caption" color="warning.main">
                  Pick at least one platform first.
                </Typography>
              )}
            </Stack>
          )}

          {/* Validation feedback — the whole point of checking at ingest. */}
          {ingestReport && (
            <Stack spacing={1}>
              {ingestReport.accepted > 0 && (
                <Alert severity="success">
                  {ingestReport.accepted} clip(s) accepted and scheduled.
                </Alert>
              )}
              {ingestReport.rejected.length > 0 && (
                <Alert severity="error">
                  <AlertTitle>
                    {ingestReport.rejected.length} clip(s) rejected — not usable as Shorts
                  </AlertTitle>
                  {ingestReport.rejected.map((r) => (
                    <Box key={r.filename}>
                      <strong>{r.filename}</strong>: {r.reasons.join('; ')}
                    </Box>
                  ))}
                </Alert>
              )}
              {ingestReport.skipped.length > 0 && (
                <Alert severity="warning">
                  <AlertTitle>Some platforms were skipped for some clips</AlertTitle>
                  {ingestReport.skipped.map((s, i) => (
                    <Box key={i}>
                      <strong>{s.filename}</strong> → {s.platform}: {s.reason}
                    </Box>
                  ))}
                </Alert>
              )}
            </Stack>
          )}
        </Stack>
      </Panel>

      {/* ── Batch list ────────────────────────────────────────────── */}
      <Panel label="BATCHES">
        {batches.isLoading ? <CircularProgress size={20} /> : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>LABEL</TableCell>
                <TableCell>SITE</TableCell>
                <TableCell align="right">CLIPS</TableCell>
                <TableCell>PROGRESS</TableCell>
                <TableCell>NEXT RELEASE</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {(batches.data?.batches ?? []).map((b: ShortsBatch) => (
                <TableRow
                  key={b.batch_id} hover selected={selected === b.batch_id}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setSelected(b.batch_id)}
                >
                  <TableCell>
                    {b.label || '(untitled)'}
                    {b.source === 'reaper' && (
                      <Chip size="small" label="REAPER" variant="outlined"
                            sx={{ ml: 1, height: 16, fontSize: 9 }} />
                    )}
                  </TableCell>
                  <TableCell>{b.site}</TableCell>
                  <TableCell align="right">{b.clip_count ?? 0}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {!!b.published_count && (
                        <Chip size="small" color="success" label={`${b.published_count} live`} />
                      )}
                      {!!b.scheduled_count && (
                        <Chip size="small" color="warning" label={`${b.scheduled_count} sched`} />
                      )}
                      {!!b.pending_count && (
                        <Chip size="small" label={`${b.pending_count} queued`} />
                      )}
                      {!!b.error_count && (
                        <Chip size="small" color="error" label={`${b.error_count} err`} />
                      )}
                      {!!b.skipped_count && (
                        <Chip size="small" variant="outlined" label={`${b.skipped_count} skip`} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {fmtWhen(b.next_release_at)}
                  </TableCell>
                  <TableCell>
                    <Button size="small" onClick={(e) => {
                      e.stopPropagation(); setSelected(b.batch_id);
                    }}>Open</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!(batches.data?.batches ?? []).length && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary">
                      No batches yet — stage one above.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Panel>

      {/* ── Batch detail ──────────────────────────────────────────── */}
      {selected && (
        <Panel label="BATCH DETAIL">
          {detail.isLoading ? <CircularProgress size={20} /> : (
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">
                  {detail.data?.label} · {detail.data?.clips?.length ?? 0} clips
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ToggleButtonGroup size="small" exclusive value={view}
                                     onChange={(_, v) => v && setView(v)}>
                    <ToggleButton value="list">List</ToggleButton>
                    <ToggleButton value="calendar"
                                  disabled={!(rotation.data?.clips ?? []).length}>
                      Calendar
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Button size="small" startIcon={<Refresh />}
                          onClick={() => { detail.refetch(); rotation.refetch(); }}>
                    Refresh
                  </Button>
                </Stack>
              </Stack>
              {view === 'calendar' && (
                <RotationCalendar clips={rotation.data?.clips ?? []} labels={clipLabels}
                                  onOpen={(id) => { setView('list'); setExpanded(id); }}
                                  onChanged={() => rotation.refetch()} />
              )}
              {view === 'list' && (<>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>TITLE</TableCell>
                    <TableCell>DUR</TableCell>
                    <TableCell>SIZE</TableCell>
                    <TableCell>SLOT</TableCell>
                    <TableCell>TARGETS</TableCell>
                    <TableCell>THUMBNAILS</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detail.data?.clips ?? []).map((c) => (
                    <React.Fragment key={c.clip_id}>
                    <TableRow hover>
                      <TableCell>{c.ordinal + 1}</TableCell>
                      <TableCell sx={{ maxWidth: 260, cursor: 'pointer' }}
                                 onClick={() => setEditing(c)}>
                        <Typography variant="body2" noWrap title={c.title}>
                          {c.title || c.filename}
                        </Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography variant="caption" color="text.secondary">
                            {c.width}×{c.height}
                          </Typography>
                          <RenderJobBadge job={c.render_job} />
                        </Stack>
                      </TableCell>
                      <TableCell>{fmtDur(c.duration_seconds)}</TableCell>
                      <TableCell>
                        {c.bytes ? `${(c.bytes / 1024 / 1024).toFixed(0)} MB` : '—'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {fmtWhen(c.scheduled_at)}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {c.targets?.map((t) => {
                            // `published` alone hid a week-late TikTok post
                            // behind a green tick (PAD-150).
                            const why = troubles(t);
                            const detail = t.error || t.platform_url || t.status;
                            return (
                            <Tooltip
                              key={t.target_id}
                              title={why.length
                                ? `${t.platform}: ${why.join(' · ')} — ${detail}`
                                : `${t.platform}: ${detail}`}
                            >
                              <Chip
                                size="small"
                                color={isOverdue(t) ? 'error'
                                  : why.length && t.status === 'published'
                                    ? 'warning' : STATUS_COLOR[t.status]}
                                variant={t.status === 'skipped' ? 'outlined' : 'filled'}
                                icon={
                                  t.status === 'published' ? <CheckCircle />
                                    : t.status === 'scheduled' ? <Schedule />
                                      : t.status === 'error' ? <ErrorOutline />
                                        : t.status === 'skipped' ? <SkipNext />
                                          : undefined
                                }
                                label={t.platform}
                                onClick={
                                  t.platform_url
                                    ? () => window.open(t.platform_url as string, '_blank')
                                    : t.status === 'error'
                                      ? () => retry.mutate(t.target_id)
                                      : undefined
                                }
                                onDelete={
                                  t.status === 'error'
                                    ? () => retry.mutate(t.target_id)
                                    : undefined
                                }
                                deleteIcon={t.status === 'error' ? <Refresh /> : undefined}
                                sx={{ height: 20, fontSize: 10 }}
                              />
                            </Tooltip>
                            );
                          })}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {rotationByClip[c.clip_id] ? (
                          <RotationStrip
                            clip={rotationByClip[c.clip_id]}
                            onClick={() => setExpanded(expanded === c.clip_id ? null : c.clip_id)}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Watch this clip before it goes out">
                          <IconButton size="small" onClick={() => setPreviewing(c)}>
                            <PlayArrow fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit title, description and release time — with a per-platform caption preview">
                          <IconButton size="small" onClick={() => setEditing(c)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Fix the framing, captions or transcript and queue a re-render in REAPER">
                          <IconButton size="small" onClick={() => setFraming(c.clip_id)}>
                            <Movie fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove this clip from the batch (does not unpublish anything already live)">
                          <IconButton size="small"
                                      onClick={() => removeClip.mutate(c.clip_id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    {expanded === c.clip_id && rotationByClip[c.clip_id] && (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ bgcolor: 'action.hover' }}>
                          <ShortsRotationPanel
                            clip={rotationByClip[c.clip_id]}
                            site={(detail.data?.site as Site) || 'spoutlore'}
                            onChanged={() => rotation.refetch()}
                            onPickFaces={() => setPicking({
                              key: c.clip_id, title: c.title || c.filename || '' })}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
              {(rotation.data?.clips ?? []).length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  <strong>Thumbnails:</strong> click a strip to see every version, when
                  it goes up, and whether each swap landed. Green = live now, blue =
                  swap requested, red = swap failed (the previous image stays up).
                  Change a day on any swap that hasn&apos;t happened yet, or use
                  the Calendar view to drag swaps between days.
                </Typography>
              )}
              </>)}
              <Typography variant="caption" color="text.secondary">
                A <strong>scheduled</strong> chip on a native-schedule platform means
                the platform itself holds the timer — the clip goes live on its slot
                even if AutoPod is down. Every tick, the worker re-checks anything
                past due and turns it red if it didn't actually publish.
              </Typography>
            </Stack>
          )}
        </Panel>
      )}
      <ThumbnailFacePicker targetKey={picking?.key ?? null} title={picking?.title}
                           onClose={() => setPicking(null)}
                           onDone={() => rotation.refetch()} />
      <ShortsClipEditor clip={editing} onClose={() => setEditing(null)} />
      <ShortsFramingEditor clipId={framing} onClose={() => setFraming(null)} />
      <ShortsPreview clipId={previewing?.clip_id ?? null}
                     targets={previewing?.targets}
                     onClose={() => setPreviewing(null)} />
    </Stack>
  );
};

export default ShortsBatchTab;
